import React from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { differenceInCalendarDays, eachMonthOfInterval, parseISO } from 'date-fns';
import { TODAY, timelineRange } from '../../data/store';
import { formatDateObject, getStatus, statusLabel } from '../../utils/exhibition';
import { easeOut, staggerDelay } from '../../utils/motion';
import type { Creator } from '../../types/catalog';
const rangeStart = parseISO(timelineRange.start);
const rangeEnd = parseISO(timelineRange.end);
const totalDays = differenceInCalendarDays(rangeEnd, rangeStart);
function pos(date: Date): number {
  return Math.min(100, Math.max(0, differenceInCalendarDays(date, rangeStart) / totalDays * 100));
}
const barClass = {
  current: 'bg-ink',
  upcoming: 'border border-dashed border-sage bg-sage/15',
  past: 'bg-line'
};
export function ExhibitionTimeline({
  creators
}: {
  creators: Creator[];
}) {
  const reduce = useReducedMotion();
  const months = eachMonthOfInterval({
    start: rangeStart,
    end: rangeEnd
  });
  const todayPos = pos(TODAY);
  const sorted = [...creators].sort((a, b) => a.exhibitionStart.localeCompare(b.exhibitionStart));
  return <div className="-mx-5 overflow-x-auto px-5 md:mx-0 md:px-0" data-id="element-248" data-mp="181vdx9u3g7x">
      <div className="relative min-w-[760px]" data-id="element-249" data-mp="15fkbnnqiiky">
        <div className="grid grid-cols-[200px_1fr] border-b border-line pb-2 md:grid-cols-[260px_1fr]" data-id="element-250" data-mp="3sdjnqmwbrheh">
          <span className="text-xs text-muted" data-id="element-251" data-mp="34pa8k3tm9rwb">Créateur</span>
          <div className="relative h-4" data-id="element-252" data-mp="2zp5uekg6l999">
            {months.map(m => <span key={m.toISOString()} className="absolute top-0 text-xs capitalize text-muted" style={{
            left: `${pos(m)}%`
          }} data-id="element-253" data-mp="2zn2th5dhpvpr">
                {formatDateObject(m, 'MMM')}
              </span>)}
          </div>
        </div>

        <div className="pointer-events-none absolute bottom-0 left-[200px] right-0 top-0 md:left-[260px]" aria-hidden data-id="element-254" data-mp="2lvoauznhvwj8">
          {months.map(m => <span key={m.toISOString()} className="absolute bottom-0 top-6 w-px bg-line/70" style={{
          left: `${pos(m)}%`
        }} data-id="element-255" data-mp="kugmv2a4kd2p" />)}
          <span className="absolute bottom-0 top-0 z-10 w-px bg-clay" style={{
          left: `${todayPos}%`
        }} data-id="element-256" data-mp="psi3lgk2w0b0">
            <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-clay px-2 py-0.5 text-[10px] font-medium text-paper" data-id="element-257" data-mp="unfktarihfyz">
              Aujourd’hui
            </span>
          </span>
        </div>

        <ul data-id="element-258" data-mp="6v8ky1mkuqut">
          {sorted.map((c, i) => {
          const status = getStatus(c);
          const left = pos(parseISO(c.exhibitionStart));
          const width = pos(parseISO(c.exhibitionEnd)) - left;
          return <li key={c.id} data-id="element-259" data-mp="fxzyq82xdypm" data-mpi={i}>
                <Link to={`/createurs/${c.id}`} className="group grid grid-cols-[200px_1fr] items-center border-b border-line/70 py-2.5 md:grid-cols-[260px_1fr]" data-id="element-260" data-mp="fu2dngwsex33">
                  <span className="pr-4" data-id="element-261" data-mp="21f9383dwfo59">
                    <span className={`block truncate text-sm font-medium group-hover:text-clay ${status === 'past' ? 'text-muted' : ''}`} data-id="element-262" data-mp="21e7gh1nitd7p">
                      {c.name}
                    </span>
                    <span className="block truncate text-xs text-muted" data-id="element-263" data-mp="1roeduq5cw8fl">{statusLabel(c)}</span>
                  </span>
                  <span className="relative block h-6" data-id="element-264" data-mp="1nnvl8tvn2ycc">
                    <motion.span className={`absolute top-1 block h-4 origin-left rounded-full ${barClass[status]} transition-opacity duration-150 group-hover:opacity-80`} style={{
                  left: `${left}%`,
                  width: `${width}%`
                }} initial={reduce ? false : {
                  scaleX: 0
                }} whileInView={{
                  scaleX: 1
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.3,
                  ease: easeOut,
                  delay: staggerDelay(i, 0.035, 0.5)
                }} data-id="element-265" data-mp="1ipaurm66vt13" />
                  </span>
                </Link>
              </li>;
        })}
        </ul>
        <div className="mt-4 flex flex-wrap gap-5 text-xs text-muted" data-id="element-266" data-mp="364e91xy7i58z">
          <span className="flex items-center gap-2" data-id="element-267" data-mp="3b5202jmfqm37">
            <span className="h-2.5 w-6 rounded-full bg-ink" data-id="element-268" data-mp="2x62ly5wjtpla" /> En vitrine
          </span>
          <span className="flex items-center gap-2" data-id="element-269" data-mp="317e2llxg30hc">
            <span className="h-2.5 w-6 rounded-full border border-dashed border-sage bg-sage/15" data-id="element-270" data-mp="315t9m0qs3pll" /> À venir
          </span>
          <span className="flex items-center gap-2" data-id="element-271" data-mp="2ndwmkb3yw5w2">
            <span className="h-2.5 w-6 rounded-full bg-line" data-id="element-272" data-mp="2s6zkswjow3il" /> Déjà passé
          </span>
        </div>
      </div>
    </div>;
}